import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { 
  Button, 
  Heading, 
  Text, 
  Container,
  Card,
} from '@shinkansen/sakura'
import styles from './home.module.css'

const FEATURES = [
  {
    title: 'THE RIGHT SIZE',
    subtitle: '3 sizes, clearly weighed',
    description: 'Choose the egg size that suits the way you cook, with clear weight ranges for every option.',
  },
  {
    title: 'JUST ENOUGH EGGS',
    subtitle: '6 eggs or a full tray',
    description: 'Order as few as six eggs or a full 30-egg tray, so you only buy what you need.',
  },
  {
    title: 'NO DELIVERY GUESSWORK',
    subtitle: 'See cost, ETA & progress',
    description: 'Check availability and delivery details before checkout, then follow your order to your door.',
  },
]

const STATS = [
  { value: '50K+', label: 'Eggs Delivered' },
  { value: '2K+', label: 'Happy Customers' },
  { value: '24H', label: 'Avg Delivery' },
  { value: '100%', label: 'Satisfaction' },
]

export function HomePage() {
  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <Container size="xl">
          <div className={styles.heroGrid}>
            {/* Left: Content */}
            <div className={styles.heroContent}>
              <Text size="xs" variant="muted" className={styles.edition}>
                TASAMAGO • PREMIUM EGGS • EST. 2025
              </Text>
              
              <Heading level="h1" size="hero" className={styles.heroTitle}>
                FRESH<br />
                <span className={styles.highlight}>EGGS</span><br />
                DELIVERED
              </Heading>
              
              <Text size="lg" variant="muted" className={styles.heroSubtitle}>
                Premium farm-fresh eggs delivered straight to your door. 
                Quality you can taste, convenience you deserve.
              </Text>

              <div className={styles.heroActions}>
                <Link to="/order/new">
                  <Button variant="accent" size="lg">
                    ORDER NOW
                  </Button>
                </Link>
                <Link to="/orders">
                  <Button variant="outline" size="lg">
                    MY ORDERS
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Egg Illustration */}
            <div className={styles.heroVisual}>
              <motion.div 
                className={styles.eggContainer}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <motion.div 
                  className={styles.eggMain}
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [-3, 3, -3],
                  }}
                  transition={{ 
                    duration: 5, 
                    repeat: Infinity,
                    ease: "easeInOut" 
                  }}
                />
                <motion.div 
                  className={styles.eggShadow}
                  animate={{ 
                    scale: [1, 0.9, 1],
                    opacity: [0.3, 0.2, 0.3],
                  }}
                  transition={{ 
                    duration: 5, 
                    repeat: Infinity,
                    ease: "easeInOut" 
                  }}
                />
              </motion.div>
              
              {/* Floating badges */}
              <motion.div 
                className={styles.badge}
                style={{ top: '10%', right: '10%' }}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0 }}
              >
                <span className={styles.badgeValue}>100%</span>
                <span className={styles.badgeLabel}>ORGANIC</span>
              </motion.div>
              
              <motion.div 
                className={styles.badge}
                style={{ bottom: '20%', left: '5%' }}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1 }}
              >
                <span className={styles.badgeValue}>24H</span>
                <span className={styles.badgeLabel}>DELIVERY</span>
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      {/* Stats Marquee */}
      <section className={styles.statsSection}>
        <div className={styles.statsMarquee}>
          <div className={styles.statsTrack}>
            {[...STATS, ...STATS, ...STATS, ...STATS].map((stat, i) => (
              <div key={i} className={styles.statItem}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
                <span className={styles.statDivider}>•</span>
              </div>
            ))}
          </div>
          <div className={styles.statsTrack} aria-hidden="true">
            {[...STATS, ...STATS, ...STATS, ...STATS].map((stat, i) => (
              <div key={i} className={styles.statItem}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
                <span className={styles.statDivider}>•</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.features}>
        <Container size="xl">
          <div className={styles.featuresHeader}>
            <Text size="xs" variant="muted" className={styles.sectionLabel}>
              WHY TASAMAGO
            </Text>
            <Heading level="h2" size="xl" className={styles.featuresTitle}>
              THE <span className={styles.highlight}>TASAMAGO</span> DIFFERENCE
            </Heading>
          </div>

          <div className={styles.featuresGrid}>
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                style={{ height: '100%' }}
              >
                <Card className={styles.featureCard} interactive="hover" size="none">
                  <div className={styles.featureLead}>{feature.subtitle}</div>
                  <div className={styles.featureContent}>
                    <Heading level="h3" size="md" className={styles.featureTitle}>
                      {feature.title}
                    </Heading>
                    <Text variant="muted" size="sm">
                      {feature.description}
                    </Text>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className={styles.cta}>
        <Container size="lg">
          <div className={styles.ctaContent}>
            <div className={styles.ctaText}>
              <Heading level="h2" size="xl" className={styles.ctaTitle}>
                READY TO<br />
                <span className={styles.highlight}>ORDER?</span>
              </Heading>
              <Text variant="muted" size="lg">
                Fresh eggs are just a click away. Start your egg journey today.
              </Text>
            </div>
            <Link to="/order/new">
              <Button variant="accent" size="lg" className={styles.ctaButton}>
                PLACE YOUR ORDER
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <Container size="xl">
          <div className={styles.footerContent}>
            <div className={styles.footerBrand}>
              <span className={styles.footerLogo}>TASAMAGO</span>
              <Text size="xs" variant="muted">
                Premium egg delivery
              </Text>
            </div>
            <div className={styles.footerLinks}>
              <Link to="/order/new" className={styles.footerLink}>Order</Link>
              <Link to="/orders" className={styles.footerLink}>My Orders</Link>
            </div>
            <Text size="xs" variant="muted" className={styles.footerCopy}>
              © 2025 TASAMAGO. All rights reserved.
            </Text>
          </div>
        </Container>
      </footer>
    </div>
  )
}
